import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "react-toastify";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import Input from "../../components/form/input/InputField";
import SubscriptionPlanTable from "../../components/tables/SubscriptionPlanTable";
import SubscriptionPlanService from "../../services/subscription-plan.service";
import { SubscriptionPlan } from "./types";

const PAGE_SIZE = 10;

const extractPlans = (responseData: any): SubscriptionPlan[] => {
  const data = responseData?.data ?? responseData;
  if (Array.isArray(data)) return data;
  return data?.plans || data?.subscriptionPlans || data?.items || [];
};

const extractTotalPages = (responseData: any, count: number): number => {
  const data = responseData?.data ?? responseData;
  const pagination = data?.pagination || responseData?.pagination;
  if (pagination?.totalPages) return Number(pagination.totalPages);
  const total = pagination?.total || responseData?.count || count;
  return Math.max(1, Math.ceil(Number(total || count) / PAGE_SIZE));
};

export default function ListSubscriptionPlans() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchPlans = useCallback(async (page: number, search = searchQuery) => {
    try {
      setLoading(true);
      setError("");
      const response = await SubscriptionPlanService.getPlans({
        page,
        limit: PAGE_SIZE,
        search,
        sortOrder: "desc",
        isActive: "",
      });
      const nextPlans = extractPlans(response.data);
      setPlans(nextPlans);
      setTotalPages(extractTotalPages(response.data, nextPlans.length));
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setError("Failed to fetch subscription plans. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPlans(1, searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [fetchPlans, searchQuery]);

  const handleToggleStatus = async (plan: SubscriptionPlan) => {
    try {
      const response = await SubscriptionPlanService.updatePlan(plan._id, {
        isActive: !plan.isActive,
      });
      if (response.data?.status === 200 || response.status === 200) {
        toast.success(`Plan ${plan.isActive ? "inactivated" : "activated"} successfully`);
        await fetchPlans(currentPage, searchQuery);
      }
    } catch (err) {
      console.error("Error updating subscription plan status:", err);
      toast.error("Failed to update plan status. Please try again.");
    }
  };

  return (
    <>
      <PageMeta
        title="VIYUME - Subscription Plans"
        description="Subscription Plan Management"
      />
      <div className="space-y-6">
        <ComponentCard title="">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <Input
                type="text"
                placeholder="Search plans"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onEnter={(value) => fetchPlans(1, value)}
                className="pl-10"
              />
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={() => {
                window.location.href = "/create-subscription-plan";
              }}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" />
              Create Plan
            </button>
          </div>

          {error ? (
            <div className="text-red-500 text-center p-4">{error}</div>
          ) : (
            <SubscriptionPlanTable
              plans={plans}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => fetchPlans(page, searchQuery)}
              onToggleStatus={handleToggleStatus}
              isLoading={loading}
            />
          )}
        </ComponentCard>
      </div>
    </>
  );
}
